import SpanishFlag from '@assets/flag_spain.svg';
import EnglishFlag from '@assets/flag_usa.svg';
import { graphQlClient } from '@config/graphqlClient';
import UserGraphQL from '@graphql/user.queries';
import { useTranslation } from '@hooks/useTranslation';
import { SnackType } from '@models/common.model';
import { updateOwnUserRequest, UserLocale } from '@models/user.model';
import { FlagOutlined } from '@mui/icons-material';
import { Button, Menu, MenuItem } from '@mui/material';
import Box from '@mui/material/Box';
import { useAppDispatch, useAppSelector } from '@redux/hooks';
import { appendActionMessage } from '@redux/reducers/actionMessages.reducer';
import { setLocale } from '@redux/reducers/locale.reducer';
import { setUserDetails } from '@redux/reducers/user.reducer';
import { RootState } from '@redux/store';
import { useState } from 'react';

interface LanguagePickerProps {}

const LanguagePicker: React.FC<LanguagePickerProps> = () => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [isLoading, setIsLoading] = useState(false);
  const localeSettings = useTranslation('settings');
  const localeCommon = useTranslation('common');
  const localeActionMessages = useTranslation('actionMessages');

  const dispatch = useAppDispatch();
  const { activeLocale } = useAppSelector((state: RootState) => state.locale);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLanguageChange = async (language: 'en' | 'es') => {
    if (language === activeLocale || isLoading) return;

    setIsLoading(true);
    handleClose();

    try {
      // Update locale in Redux immediately for responsive UI
      dispatch(setLocale(language));

      // Save to backend
      const updateUser: updateOwnUserRequest = await graphQlClient.request(UserGraphQL.mutations.updateOwnUser, {
        user: { locale: language as UserLocale },
      });

      if (updateUser.updateOwnUser) {
        // Update user details in Redux with the saved locale
        dispatch(
          setUserDetails({
            ...updateUser.updateOwnUser,
            locale: updateUser.updateOwnUser.locale,
          }),
        );

        dispatch(
          appendActionMessage({
            message: localeActionMessages['languageUpdatedSuccessfully'] || 'Language updated successfully',
            type: SnackType.SUCCESS,
          }),
        );
      }
    } catch (error) {
      console.error('Failed to save language preference:', error);
      // Revert locale change on error
      dispatch(setLocale(activeLocale));
      dispatch(
        appendActionMessage({
          message: localeCommon['requestError'],
          type: SnackType.ERROR,
        }),
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box className="flex flex-wrap justify-center">
      <Button
        className="text-white"
        aria-controls="language-menu"
        aria-haspopup="true"
        onClick={handleClick}
        disabled={isLoading}
        startIcon={<FlagOutlined className="icon" />}>
        {activeLocale === 'en' ? (
          <img src={EnglishFlag} alt="English Flag" height={20} width={40} />
        ) : (
          <img src={SpanishFlag} alt="Spanish Flag" height={20} width={40} />
        )}
      </Button>
      <Menu id="language-menu" anchorEl={anchorEl} keepMounted open={Boolean(anchorEl)} onClose={handleClose}>
        <MenuItem
          onClick={() => {
            handleLanguageChange('en');
          }}>
          <img src={EnglishFlag} alt="English Flag" height={20} width={30} className="mr-2" />
          {localeSettings['english']}
        </MenuItem>
        <MenuItem onClick={() => handleLanguageChange('es')}>
          <img src={SpanishFlag} alt="Spanish Flag" height={20} width={30} className="mr-2" />
          {localeSettings['spanish']}
        </MenuItem>
      </Menu>
    </Box>
  );
};

export default LanguagePicker;
